import { useQuery } from "@tanstack/react-query";
import { Link2, Zap, ShieldCheck, AlertTriangle, RefreshCw, Activity, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface ChainIntegrity {
  score: number;
  tier: string;
  label: string;
}

interface ChainLink {
  id: string;
  from_node_id: string;
  to_node_id: string;
  relation_type: string;
  link_order: number;
  is_refined: boolean;
}

interface ChainEvent {
  id: string;
  event_type: string;
  event_note: string | null;
  score_snapshot: Record<string, unknown>;
  created_at: string;
}

interface Chain {
  id: string;
  session_id: string;
  status: string;
  observer_gate_state: string;
  source_authorized: boolean;
  chain_integrity_score: number;
  lightning_path_ready: boolean;
  bell_state_active: boolean;
  break_detected: boolean;
  break_reason: string | null;
  refinement_required: boolean;
  created_at: string;
}

interface ChainData {
  chain: Chain;
  integrity: ChainIntegrity;
  links: ChainLink[];
  events: ChainEvent[];
}

function getStatusColor(status: string): string {
  switch (status) {
    case "active":
    case "authorized":
    case "refined":
      return "text-emerald-400";
    case "bell_state":
      return "text-amber-300";
    case "candidate":
    case "held":
    case "partial":
      return "text-blue-400";
    case "broken":
    case "counterfeit":
      return "text-red-400";
    default:
      return "text-zinc-400";
  }
}

function getTierColor(tier: string): string {
  switch (tier) {
    case "high":
      return "text-emerald-400";
    case "usable":
      return "text-blue-400";
    case "unstable":
      return "text-amber-400";
    case "compromised":
      return "text-orange-400";
    case "broken":
      return "text-red-400";
    default:
      return "text-zinc-400";
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case "active":
    case "authorized":
    case "refined":
      return <ShieldCheck className="w-4 h-4 text-emerald-400" />;
    case "bell_state":
      return <Zap className="w-4 h-4 text-amber-300" />;
    case "broken":
    case "counterfeit":
      return <AlertTriangle className="w-4 h-4 text-red-400" />;
    case "candidate":
    case "held":
      return <RefreshCw className="w-4 h-4 text-blue-400" />;
    default:
      return <Link2 className="w-4 h-4 text-zinc-400" />;
  }
}

export function ResonanceChainPanel({ userId }: { userId: string }) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery<ChainData[]>({
    queryKey: ["/api/resonance-chains/user", userId],
    queryFn: async () => {
      const res = await fetch(`/api/resonance-chains/user/${encodeURIComponent(userId)}?limit=5`);
      if (!res.ok) return [];
      const chains: Chain[] = await res.json();
      if (!chains.length) return [];

      const detailed = await Promise.all(
        chains.slice(0, 3).map(async (c) => {
          const detailRes = await fetch(`/api/resonance-chains/${c.id}`);
          if (!detailRes.ok) return null;
          return detailRes.json() as Promise<ChainData>;
        })
      );
      return detailed.filter(Boolean) as ChainData[];
    },
    refetchInterval: 15000,
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div data-testid="resonance-chain-panel-skeleton" className="rounded-lg border border-zinc-700/50 bg-zinc-900/60 backdrop-blur-sm p-4 animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 rounded bg-zinc-700" />
          <div className="h-4 w-32 rounded bg-zinc-700" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-zinc-800" />
          <div className="h-3 w-3/4 rounded bg-zinc-800" />
          <div className="h-3 w-1/2 rounded bg-zinc-800" />
        </div>
      </div>
    );
  }
  if (!data || data.length === 0) return null;

  const latest = data[0];

  return (
    <div
      data-testid="resonance-chain-panel"
      className="rounded-lg border border-zinc-700/50 bg-zinc-900/60 backdrop-blur-sm overflow-hidden"
    >
      <button
        data-testid="chain-panel-toggle"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-medium text-zinc-300">Resonance Chain</span>
          <span className={`text-xs font-mono ${getStatusColor(latest.chain.status)}`}>
            {latest.chain.status.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-mono ${getTierColor(latest.integrity.tier)}`}>
            {latest.chain.chain_integrity_score.toFixed(1)}
          </span>
          {latest.chain.lightning_path_ready && (
            <Zap className="w-3 h-3 text-amber-300" data-testid="lightning-indicator" />
          )}
          {expanded ? (
            <ChevronUp className="w-3 h-3 text-zinc-500" />
          ) : (
            <ChevronDown className="w-3 h-3 text-zinc-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-zinc-700/30">
          <div className="pt-2 grid grid-cols-2 gap-2 text-xs">
            <div className="space-y-1">
              <div className="text-zinc-500">Status</div>
              <div className="flex items-center gap-1">
                {getStatusIcon(latest.chain.status)}
                <span className={getStatusColor(latest.chain.status)}>
                  {latest.chain.status}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-zinc-500">Integrity</div>
              <div className={`font-mono ${getTierColor(latest.integrity.tier)}`}>
                {latest.integrity.score.toFixed(2)} ({latest.integrity.tier})
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-zinc-500">Observer Gate</div>
              <div className="text-zinc-300 font-mono">{latest.chain.observer_gate_state}</div>
            </div>
            <div className="space-y-1">
              <div className="text-zinc-500">Authorization</div>
              <div className={latest.chain.source_authorized ? "text-emerald-400" : "text-zinc-500"}>
                {latest.chain.source_authorized ? "Authorized" : "Pending"}
              </div>
            </div>
          </div>

          {latest.chain.bell_state_active && (
            <div
              data-testid="bell-state-indicator"
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-amber-900/20 border border-amber-600/30 text-xs text-amber-300"
            >
              <Zap className="w-3 h-3" />
              Bell State Active — Sustained harmonic coherence
            </div>
          )}

          {latest.chain.break_detected && (
            <div
              data-testid="chain-break-indicator"
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-red-900/20 border border-red-600/30 text-xs text-red-400"
            >
              <AlertTriangle className="w-3 h-3" />
              Break: {latest.chain.break_reason || "Unknown cause"}
            </div>
          )}

          {latest.links.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-zinc-500 font-medium">Chain Links</div>
              <div className="flex items-center gap-0.5 flex-wrap">
                {latest.links.map((link, i) => {
                  const fromLabel = link.from_node_id.split("-")[0];
                  const toLabel = link.to_node_id.split("-")[0];
                  const refined = link.is_refined;
                  return (
                    <div key={link.id} className="flex items-center gap-0.5" data-testid={`chain-link-${i}`}>
                      {i === 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${refined ? "bg-emerald-900/30 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>
                          {fromLabel}
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-600">→</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${refined ? "bg-emerald-900/30 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>
                        {toLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {latest.events.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-zinc-500 font-medium">Recent Events</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {latest.events.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-start gap-2 text-[10px]"
                    data-testid={`chain-event-${event.event_type}`}
                  >
                    <span className="text-zinc-600 whitespace-nowrap">
                      {new Date(event.created_at).toLocaleTimeString()}
                    </span>
                    <span className="text-zinc-400 truncate">
                      {event.event_note || event.event_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
